import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Listbox } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import CloseButton from '@/components/UI/Buttons/CloseButton';
import { classNames, generateBffErrorMessageWithCode } from '@/utils/utils';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import {
  BffSubdivisionsResponseV2,
  BffSubdivisionV2,
  BffUserResponse,
} from '@lucky7ventures/bff-types';
import BffApiService from '@/shared/bffApiService';
import { setUser } from '@/store/actions/user';
import { BaseModalProps } from '@/features/modal/types';

export const UpdateStateModal = ({ closeModal }: BaseModalProps): JSX.Element => {
  const intl = useIntl();
  const [province, setProvince] = useState<BffSubdivisionV2 | null>(null);
  const {
    request: updateUserRequest,
    error: updateUserError,
    loading: updateUserLoading,
  } = useBffApiRequest<BffUserResponse>();
  const { request: subdivisionsRequest, data: provinces } =
    useBffApiRequest<BffSubdivisionsResponseV2>();
  const dispatch = useDispatch();

  const closeHandler = () => {
    closeModal();
  };

  useEffect(() => {
    subdivisionsRequest({
      apiMethod: BffApiService.getSubdivisions,
      pathParams: {
        countryCode: 'CA',
      },
      successCallback: data => {
        setProvince(data[0]);
      },
    });
  }, []);

  const submit = () => {
    updateUserRequest({
      apiMethod: BffApiService.updateUser,
      payload: {
        address: { subdivisionCode: province?.code },
      },
      successCallback: (user: BffUserResponse) => {
        dispatch(setUser(user));
        closeModal();
      },
    });
  };

  return (
    <div className="prose relative flex h-[580px] w-full max-w-sm flex-col justify-between space-y-4 rounded-2xl bg-gray-50 p-8 pt-0 text-left">
      <CloseButton right onClick={() => closeHandler()} />
      <div>
        <h2 className="mb-0">{intl.formatMessage({ id: 'state.update.modal.title' })}</h2>
        <p className="mt-4">{intl.formatMessage({ id: 'state.update.modal.subtitle' })}</p>
        <div className="mt-8 font-bold">{intl.formatMessage({ id: 'input.province.label' })}</div>
        <Listbox value={province} onChange={setProvince}>
          <div className="relative mt-2">
            <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white py-2 pl-3 pr-10 text-left shadow-md">
              <span className="block truncate">{province?.name}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            </Listbox.Button>
            <Listbox.Options className="text-m absolute z-10 mt-1 max-h-60 w-full space-y-1 overflow-auto rounded-md bg-white p-2 shadow-lg">
              {provinces
                ?.sort((a, b) => a.name.localeCompare(b.name))
                .map(province => (
                  <Listbox.Option
                    key={province.code}
                    value={province}
                    className={({ active, selected }) =>
                      classNames(
                        selected
                          ? 'bg-blue-light font-bold text-white'
                          : active
                          ? 'bg-gray-100'
                          : 'bg-white',
                        'm-0 cursor-pointer rounded-md px-2 py-1',
                      )
                    }
                  >
                    {province.name}
                  </Listbox.Option>
                ))}
            </Listbox.Options>
          </div>
        </Listbox>
        {updateUserError && (
          <p className="text-red-600">
            {generateBffErrorMessageWithCode(
              intl.formatMessage({ id: 'error.support' }),
              updateUserError.code,
            )}
          </p>
        )}
      </div>
      <div className="pt-4">
        <button
          type="button"
          className="flex h-14 w-full items-center justify-center rounded-full bg-blue-light px-5 font-bold text-white shadow-md transition-colors hover:bg-cyan-600"
          onClick={submit}
        >
          {updateUserLoading ? (
            <svg
              className="h-5 w-5 animate-spin text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            intl.formatMessage({ id: 'misc.update' })
          )}
        </button>
      </div>
    </div>
  );
};
